"use client"

import type { DateRange, Matcher } from "react-day-picker"
import { addDays, format, isWithinInterval, setDate } from "date-fns"
import { CalendarIcon } from "lucide-react"
import { useTranslations } from "next-intl"
import * as React from "react"
import { Button } from "@/components/ui/button"
import { Calendar } from "@/components/ui/calendar"
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover"
import { cn } from "@/lib/utils"

const defaultDateRange = {
  from: addDays(new Date(), -20),
  to: new Date(),
}

export default function DateRangePicker({
  className,
  dateRange,
  setDateRange,
  placeholder,
  disabledDates,
}: React.HTMLAttributes<HTMLDivElement> & {
  dateRange: DateRange | undefined
  setDateRange: (dateRange: DateRange) => void
  placeholder?: string
  disabledDates?: Date[]
}) {
  const t = useTranslations("generic")
  
  // Convert disabled dates to proper matcher format
  const disabledMatchers: Matcher[] = React.useMemo(() => {
    if (!disabledDates || disabledDates.length === 0) return []
    
    return disabledDates.map(date => ({
      from: date,
      to: date,
    }))
  }, [disabledDates])
  
  return (
    <div className={cn("grid gap-2", className)}>
      <Popover>
        <PopoverTrigger asChild>
          <Button
            className={cn(
              "w-full justify-start text-left font-normal",
              !dateRange && "text-muted-foreground",
            )}
            id="date"
            variant="outline"
          >
            <CalendarIcon className="mr-2 h-4 w-4" />
            {dateRange?.from
              ? (
                  dateRange.to
                    ? (
                        <>
                          {format(dateRange.from, "LLL dd, y")}
                          {" "}
                          -
                          {" "}
                          {format(dateRange.to, "LLL dd, y")}
                        </>
                      )
                    : (
                        format(dateRange.from, "LLL dd, y")
                      )
                )
              : (
                  <span>{placeholder ?? t("pick_a_date")}</span>
                )}
          </Button>
        </PopoverTrigger>
        <PopoverContent align="start" className="w-auto p-0">
          <Calendar
            autoFocus
            defaultMonth={dateRange?.from}
            disabled={disabledMatchers}
            mode="range"
            numberOfMonths={2}
            onSelect={setDateRange}
            required
            selected={dateRange}
          />
        </PopoverContent>
      </Popover>
    </div>
  )
}
